import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CandidateTabContent = ({ candidate }) => {
  const [activeTab, setActiveTab] = useState('cv-preview');

  const tabs = [
    { id: 'cv-preview', label: 'CV Preview', icon: 'FileText' },
    { id: 'ai-analysis', label: 'AI Analysis', icon: 'Brain' },
    { id: 'interview-notes', label: 'Interview Notes', icon: 'MessageSquare' },
    { id: 'communication', label: 'Communication', icon: 'Mail' }
  ];

  const renderCVPreview = () => (
    <div className="space-y-6">
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-foreground">Original CV Document</h3>
          <div className="flex space-x-2">
            <button className="p-2 hover:bg-muted rounded-lg transition-colors micro-feedback">
              <Icon name="Download" size={16} />
            </button>
            <button className="p-2 hover:bg-muted rounded-lg transition-colors micro-feedback">
              <Icon name="ExternalLink" size={16} />
            </button>
          </div>
        </div>
        <div className="bg-surface border border-border rounded-lg p-6 min-h-96">
          <div className="text-center text-muted-foreground">
            <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">CV Document Preview</p>
            <p className="text-sm">Click to view full document or download PDF</p>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="font-medium text-card-foreground mb-3">Key Highlights</h4>
          <ul className="space-y-2 text-sm">
            {candidate?.keyHighlights?.map((highlight, index) => (
              <li key={index} className="flex items-start space-x-2">
                <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">{highlight}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="font-medium text-card-foreground mb-3">Technical Skills</h4>
          <div className="flex flex-wrap gap-2">
            {candidate?.skills?.map((skill, index) => (
              <span 
                key={index}
                className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAIAnalysis = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg p-6 border border-primary/10">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Brain" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">AI Analysis Summary</h3>
            <p className="text-sm text-muted-foreground">Generated on {candidate?.analysisDate}</p>
          </div>
        </div>
        <p className="text-muted-foreground leading-relaxed">{candidate?.aiSummary}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="font-medium text-foreground">Scoring Breakdown</h4>
          {candidate?.scoringBreakdown?.map((item, index) => (
            <div key={index} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-card-foreground">{item?.category}</span>
                <span className={`font-bold ${getScoreColor(item?.score)}`}>{item?.score}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 mb-2">
                <div 
                  className={`h-2 rounded-full ${getScoreBarColor(item?.score)}`}
                  style={{ width: `${item?.score}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground">{item?.feedback}</p>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <h4 className="font-medium text-foreground">Recommendations</h4>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="space-y-3">
              {candidate?.recommendations?.map((rec, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <Icon 
                    name={rec?.type === 'positive' ? 'ThumbsUp' : rec?.type === 'negative' ? 'ThumbsDown' : 'AlertCircle'} 
                    size={16} 
                    className={`mt-0.5 ${
                      rec?.type === 'positive' ? 'text-success' : 
                      rec?.type === 'negative' ? 'text-destructive' : 'text-warning'
                    }`}
                  />
                  <div>
                    <p className="text-sm font-medium text-card-foreground">{rec?.title}</p>
                    <p className="text-sm text-muted-foreground">{rec?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderInterviewNotes = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">Interview History</h3>
        <button className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors micro-feedback">
          <Icon name="Plus" size={16} />
          <span>Add Note</span>
        </button>
      </div>

      <div className="space-y-4">
        {candidate?.interviewNotes?.map((note, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Image
                  src={note?.interviewer?.avatar}
                  alt={note?.interviewer?.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium text-card-foreground">{note?.interviewer?.name}</p>
                  <p className="text-sm text-muted-foreground">{note?.date} • {note?.type}</p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                note?.rating >= 4 ? 'status-excellent' :
                note?.rating >= 3 ? 'status-good' :
                note?.rating >= 2 ? 'status-needs-review' : 'status-rejected'
              }`}>
                Rating: {note?.rating}/5
              </div>
            </div>
            <p className="text-muted-foreground mb-3">{note?.feedback}</p>
            {note?.tags && (
              <div className="flex flex-wrap gap-2">
                {note?.tags?.map((tag, tagIndex) => (
                  <span key={tagIndex} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderCommunication = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">Communication History</h3>
        <button className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors micro-feedback">
          <Icon name="Send" size={16} />
          <span>Send Message</span>
        </button>
      </div>

      <div className="space-y-4">
        {candidate?.communications?.map((comm, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                comm?.type === 'email' ? 'bg-blue-100 text-blue-600' :
                comm?.type === 'call'? 'bg-green-100 text-green-600' : 'bg-purple-100 text-purple-600'
              }`}>
                <Icon 
                  name={comm?.type === 'email' ? 'Mail' : comm?.type === 'call' ? 'Phone' : 'MessageSquare'} 
                  size={16} 
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-card-foreground">{comm?.subject}</p>
                  <span className="text-sm text-muted-foreground">{comm?.date}</span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{comm?.content}</p>
                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                  <span>From: {comm?.from}</span>
                  <span>To: {comm?.to}</span>
                  <span className={`px-2 py-1 rounded ${
                    comm?.status === 'sent' ? 'bg-success/10 text-success' :
                    comm?.status === 'delivered'? 'bg-blue-100 text-blue-600' : 'bg-muted text-muted-foreground'
                  }`}>
                    {comm?.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-success';
    if (score >= 70) return 'text-accent';
    if (score >= 50) return 'text-warning';
    return 'text-destructive';
  };

  const getScoreBarColor = (score) => {
    if (score >= 85) return 'bg-success';
    if (score >= 70) return 'bg-accent';
    if (score >= 50) return 'bg-warning';
    return 'bg-destructive';
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'cv-preview':
        return renderCVPreview();
      case 'ai-analysis':
        return renderAIAnalysis();
      case 'interview-notes':
        return renderInterviewNotes();
      case 'communication':
        return renderCommunication();
      default:
        return renderCVPreview();
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="flex space-x-8 px-6">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`
                flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors micro-feedback
                ${activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                }
              `}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </nav>
      </div>
      {/* Tab Content */}
      <div className="p-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default CandidateTabContent;